import React from 'react';
import { Pagination } from '@components/molecules/Pagination';
import { Geofence, GeofenceShapeType } from '@models/geofence.model';
import {
  GeofenceCategoryIcon,
  DEFAULT_GEOFENCE_COLOR,
} from '@utils/geofenceCategoryIcons';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import RoomIcon from '@mui/icons-material/Room';
import PolylineIcon from '@mui/icons-material/Polyline';
import { useTranslation } from '@hooks/useTranslation';

interface GeofenceTableProps {
  geofences: Geofence[];
  selectedId?: string | null;
  onSelect?: (geofence: Geofence) => void;
  onEdit?: (geofence: Geofence) => void;
  onDelete?: (geofence: Geofence) => void;
  onToggleActive?: (geofence: Geofence) => void;

  /** Pagination — when these are provided, the table renders a paginated slice + footer */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;

  /** When true, the table fills its parent vertically. Defaults to true. */
  fillHeight?: boolean;
  /**
   * Compact mode renders only Name / Category / Actions. Used on pages that
   * pair the table with a side info panel where the extra columns would be
   * redundant.
   */
  compact?: boolean;
}

const ShapeBadge: React.FC<{ shapeType: GeofenceShapeType }> = ({ shapeType }) => {
  // Sprint 29, story-148. Colors/icons unchanged — text-only fix.
  const tEnumLabels = useTranslation('enumLabels');
  if (shapeType === GeofenceShapeType.POINT_RADIUS) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-50 text-blue-700">
        <RoomIcon sx={{ fontSize: 13 }} />
        {tEnumLabels.shapeTypeCircle}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-purple-50 text-purple-700">
      <PolylineIcon sx={{ fontSize: 13 }} />
      {tEnumLabels.shapeTypePolygon}
    </span>
  );
};

export const GeofenceTable: React.FC<GeofenceTableProps> = ({
  geofences,
  selectedId,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
  page,
  pageSize,
  total,
  onPageChange,
  fillHeight = true,
  compact = false,
}) => {
  const showPagination =
    typeof page === 'number' &&
    typeof pageSize === 'number' &&
    typeof total === 'number' &&
    !!onPageChange &&
    total > pageSize;

  const containerClasses = `bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col ${
    fillHeight ? 'h-full' : ''
  }`;

  if (geofences.length === 0) {
    return (
      <div className={containerClasses}>
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500 py-12">
          No geofences match the current filters.
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              {!compact && (
                <>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Shape
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Linked
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </>
              )}
              <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {geofences.map((geofence) => {
              const categoryColor =
                geofence.category?.color || geofence.color || DEFAULT_GEOFENCE_COLOR;
              const linkedLabel = geofence.project?.name
                ? `Project: ${geofence.project.name}`
                : geofence.warehouse?.name
                ? `Warehouse: ${geofence.warehouse.name}`
                : '—';
              const isSelected = selectedId === geofence.geofenceId;

              return (
                <tr
                  key={geofence.geofenceId}
                  onClick={() => onSelect?.(geofence)}
                  className={`cursor-pointer transition-colors ${
                    isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: categoryColor }}
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {geofence.name}
                        </span>
                        {compact && geofence.project?.name && (
                          <span className="text-[11px] text-gray-500">
                            Project: {geofence.project.name}
                          </span>
                        )}
                        {compact &&
                          !geofence.project?.name &&
                          geofence.warehouse?.name && (
                            <span className="text-[11px] text-gray-500">
                              Warehouse: {geofence.warehouse.name}
                            </span>
                          )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {geofence.category ? (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-md flex items-center justify-center"
                          style={{
                            backgroundColor: `${categoryColor}20`,
                            color: categoryColor,
                          }}
                        >
                          <GeofenceCategoryIcon
                            iconKey={geofence.category.icon}
                            sx={{ fontSize: 14 }}
                          />
                        </div>
                        <span className="text-sm text-gray-700">
                          {geofence.category.name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  {!compact && (
                    <>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <ShapeBadge shapeType={geofence.shape.type} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {linkedLabel}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full ${
                            geofence.isActive
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {geofence.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-1">
                      {onToggleActive && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleActive(geofence);
                          }}
                          className={`p-1.5 rounded-md hover:bg-indigo-50 ${
                            geofence.isActive
                              ? 'text-indigo-600 hover:text-indigo-700'
                              : 'text-gray-400 hover:text-indigo-600'
                          }`}
                          title={geofence.isActive ? 'Disable' : 'Enable'}
                        >
                          {geofence.isActive ? (
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <VisibilityOffIcon sx={{ fontSize: 16 }} />
                          )}
                        </button>
                      )}
                      {onEdit && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(geofence);
                          }}
                          className="p-1.5 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                          title="Edit geofence"
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(geofence);
                          }}
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                          title="Delete geofence"
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex-shrink-0 border-t border-gray-200 px-4">
          <Pagination
            page={page!}
            take={pageSize!}
            total={total!}
            setPage={onPageChange!}
          />
        </div>
      )}
    </div>
  );
};

export default GeofenceTable;
